import { useState, useRef, useContext } from "react";
import { toast } from "react-toastify";
import { ImageSquare, LinkSimple, VideoCamera, X } from "@phosphor-icons/react";
import CustomToast from "src/components/CustomToast/CustomToast";
import { Button } from "src/components/ui/button";
import { useAzureFileUpload } from "src/components/AssetUploaders/useAzureFileUpload";
import AuthContext from "src/contexts/AuthContext/AuthContext";
import { useAvatar } from "src/hooks";
import useSocialProfile from "src/hooks/useSocialProfile/useSocialProfile";
import { useCreatePost } from "src/features/SocialFeedContent/hooks";
import MentionHashtagInput from "../MentionHashtagInput/MentionHashtagInput";

interface MediaFile {
	file: File;
	preview: string;
	type: "image" | "video";
}

interface UploadedUuid {
	uuid: string;
	type: "image" | "video";
}

const ComposePost = () => {
	const { user } = useContext(AuthContext);
	const { data: socialProfile } = useSocialProfile(undefined, {
		enabled: true
	});
	const username = socialProfile?.user?.username;
	const { data: avatarUrl } = useAvatar({
		userId: username,
		enabled: Boolean(username)
	});

	const [isExpanded, setIsExpanded] = useState(false);
	const [body, setBody] = useState("");
	const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
	const [uploadedUuids, setUploadedUuids] = useState<UploadedUuid[]>([]);
	const [isDragging, setIsDragging] = useState(false);
	const { mutate: createPost, isLoading } = useCreatePost();

	const imageInputRef = useRef<HTMLInputElement>(null);
	const videoInputRef = useRef<HTMLInputElement>(null);

	const { upload: uploadFile, uploading } = useAzureFileUpload((uuid) => {
		if (!uuid) return;
		const resolvedUuid = Array.isArray(uuid) ? uuid[0] : uuid;
		if (resolvedUuid) {
			setUploadedUuids((prev) => [
				...prev,
				{
					uuid: resolvedUuid,
					type: pendingUploadType.current
				}
			]);
		}
	});

	const pendingUploadType = useRef<"image" | "video">("image");

	const processFiles = (files: File[]) => {
		const accepted: MediaFile[] = [];

		for (const file of files) {
			const isVideo = file.type.startsWith("video/");
			const isImage = file.type.startsWith("image/");

			if (!isVideo && !isImage) continue;

			const existing = [...mediaFiles, ...accepted];

			// Enforce mutual exclusivity
			if (isVideo && existing.some((m) => m.type === "image")) continue;
			if (isImage && existing.some((m) => m.type === "video")) continue;
			// Only one video allowed
			if (isVideo && existing.some((m) => m.type === "video")) continue;

			const preview = URL.createObjectURL(file);
			const mediaType: "image" | "video" = isVideo ? "video" : "image";
			accepted.push({ file, preview, type: mediaType });
		}

		if (accepted.length === 0) return;

		setMediaFiles((prev) => [...prev, ...accepted]);

		for (const media of accepted) {
			pendingUploadType.current = media.type;
			// Videos don't use an image profile (asset service handles them separately)
			uploadFile(
				media.file,
				media.type === "video" ? "video" : "image",
				undefined,
				media.type === "image" ? "retail-app-social-image" : undefined
			);
		}
	};

	const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files) return;
		processFiles(Array.from(e.target.files));
		e.target.value = "";
	};

	const handleVideoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files) return;
		processFiles(Array.from(e.target.files));
		e.target.value = "";
	};

	const removeMedia = (index: number) => {
		setMediaFiles((prev) => {
			const removed = prev[index];
			if (removed) URL.revokeObjectURL(removed.preview);
			return prev.filter((_, i) => i !== index);
		});
		setUploadedUuids((prev) => prev.filter((_, i) => i !== index));
	};

	const resetComposer = () => {
		setBody("");
		mediaFiles.forEach((m) => URL.revokeObjectURL(m.preview));
		setMediaFiles([]);
		setUploadedUuids([]);
		setIsExpanded(false);
	};

	const handleSubmit = () => {
		if (isLoading) return;
		if (!body.trim() && uploadedUuids.length === 0) return;

		const imageUuids = uploadedUuids
			.filter((u) => u.type === "image")
			.map((u) => u.uuid);
		const videoUuids = uploadedUuids
			.filter((u) => u.type === "video")
			.map((u) => u.uuid);

		createPost(
			{
				body: body.trim(),
				...(imageUuids.length > 0 ? { images: imageUuids } : {}),
				...(videoUuids.length > 0 ? { videos: videoUuids } : {})
			},
			{
				onSuccess: () => {
					toast.success(
						<CustomToast type="Success" message="Post published" />
					);
					resetComposer();
				},
				onError: () => {
					toast.error(
						<CustomToast type="Error" message="Failed to publish post" />
					);
				}
			}
		);
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = () => {
		setIsDragging(false);
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);

		const files = Array.from(e.dataTransfer.files);
		processFiles(files);

		if (!isExpanded) setIsExpanded(true);
	};

	const detectedUrls = body.match(/https?:\/\/[^\s]+/g) || [];

	const hasImages = mediaFiles.some((m) => m.type === "image");
	const hasVideo = mediaFiles.some((m) => m.type === "video");
	const isUploading = uploading || mediaFiles.length > uploadedUuids.length;

	if (!isExpanded) {
		return (
			<div
				className={[
					"rounded-xl border bg-white shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow",
					isDragging ? "border-primary border-dashed" : "border-border"
				].join(" ")}
				onClick={() => setIsExpanded(true)}
				role="button"
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						setIsExpanded(true);
					}
				}}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
			>
				<div className="flex items-center gap-3">
					{avatarUrl ? (
						<img
							src={avatarUrl}
							alt={user?.name ?? ""}
							className="w-9 h-9 rounded-full object-cover shrink-0"
						/>
					) : (
						<div className="w-9 h-9 rounded-full bg-zinc-200 text-zinc-600 flex items-center justify-center text-xs font-medium shrink-0">
							{user?.name
								?.split(" ")
								.slice(0, 2)
								.map((n) => n[0])
								.join("")
								.toUpperCase() ?? ""}
						</div>
					)}
					<span className="text-sm text-muted-foreground">
						What&apos;s on your mind?
					</span>
				</div>
			</div>
		);
	}

	return (
		<div
			className={[
				"rounded-xl border bg-white shadow-sm overflow-hidden",
				isDragging ? "border-primary border-dashed" : "border-border"
			].join(" ")}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
		>
			{/* User header */}
			<div className="px-4 pt-4 pb-2 flex items-center gap-3">
				{avatarUrl ? (
					<img
						src={avatarUrl}
						alt={user?.name ?? ""}
						className="w-9 h-9 rounded-full object-cover shrink-0"
					/>
				) : (
					<div className="w-9 h-9 rounded-full bg-zinc-200 text-zinc-600 flex items-center justify-center text-xs font-medium shrink-0">
						{user?.name
							?.split(" ")
							.slice(0, 2)
							.map((n) => n[0])
							.join("")
							.toUpperCase() ?? ""}
					</div>
				)}
				<div>
					<p className="text-sm font-medium text-foreground">
						{user?.name ?? "Unknown user"}
					</p>
					{user?.role && (
						<p className="text-[13px] text-muted-foreground">{user.role}</p>
					)}
				</div>
			</div>

			<div className="px-4 pb-2">
				<MentionHashtagInput
					value={body}
					onChange={setBody}
					onKeyDown={(e) => {
						if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
							e.preventDefault();
							handleSubmit();
						}
						if (e.key === "Escape" && !body.trim() && mediaFiles.length === 0) {
							setIsExpanded(false);
						}
					}}
					placeholder="What's on your mind?"
					className="w-full bg-white min-h-[120px] rounded-md border border-input px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none field-sizing-content"
					autoFocus
				/>
			</div>

			{/* URL preview pills */}
			{detectedUrls.length > 0 && (
				<div className="px-4 pb-2 flex flex-wrap gap-1.5">
					{detectedUrls.map((url, i) => {
						let hostname = url;
						try {
							hostname = new URL(url).hostname;
						} catch {
							/* keep raw url */
						}
						return (
							<span
								key={i}
								className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-xs px-2.5 py-0.5 font-medium"
							>
								<LinkSimple size={10} weight="duotone" />
								{hostname}
							</span>
						);
					})}
				</div>
			)}

			{/* Media toolbar */}
			<div className="px-4 pb-2 flex items-center gap-2">
				<button
					type="button"
					className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
					onClick={() => imageInputRef.current?.click()}
					disabled={hasVideo}
					aria-label="Add images"
				>
					<ImageSquare size={20} weight="duotone" />
				</button>
				<button
					type="button"
					className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
					onClick={() => videoInputRef.current?.click()}
					disabled={hasImages}
					aria-label="Add video"
				>
					<VideoCamera size={20} weight="duotone" />
				</button>
			</div>

			{/* Hidden file inputs */}
			<input
				ref={imageInputRef}
				type="file"
				accept="image/jpeg,image/png"
				multiple
				className="hidden"
				onChange={handleImageSelect}
			/>
			<input
				ref={videoInputRef}
				type="file"
				accept="video/mp4,video/quicktime"
				className="hidden"
				onChange={handleVideoSelect}
			/>

			{/* Media preview area */}
			{mediaFiles.length > 0 && (
				<div className="flex flex-wrap gap-2 px-4 pb-2">
					{mediaFiles.map((media, index) =>
						media.type === "image" ? (
							<div key={media.preview} className="relative">
								<img
									src={media.preview}
									alt={`Upload preview ${index + 1}`}
									className="w-20 h-20 rounded-lg object-cover"
								/>
								<button
									type="button"
									onClick={() => removeMedia(index)}
									className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-foreground text-white flex items-center justify-center hover:bg-foreground/80 transition-colors"
									aria-label={`Remove image ${index + 1}`}
								>
									<X size={12} weight="bold" />
								</button>
							</div>
						) : (
							<div
								key={media.preview}
								className="flex items-center gap-2 rounded-full bg-muted px-3 py-1.5 text-sm text-foreground"
							>
								<VideoCamera
									size={16}
									weight="duotone"
									className="text-muted-foreground"
								/>
								<span className="max-w-[200px] truncate">
									{media.file.name}
								</span>
								<button
									type="button"
									onClick={() => removeMedia(index)}
									className="ml-1 w-5 h-5 rounded-full bg-foreground text-white flex items-center justify-center hover:bg-foreground/80 transition-colors"
									aria-label="Remove video"
								>
									<X size={12} weight="bold" />
								</button>
							</div>
						)
					)}
				</div>
			)}

			<div className="px-4 pb-4 flex items-center justify-end gap-2">
				<Button type="button" variant="ghost" size="sm" onClick={resetComposer}>
					Cancel
				</Button>
				<Button
					type="button"
					size="sm"
					disabled={
						(!body.trim() && uploadedUuids.length === 0) ||
						isLoading ||
						isUploading
					}
					onClick={handleSubmit}
				>
					{isLoading ? (
						<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
					) : (
						"Post"
					)}
				</Button>
			</div>
		</div>
	);
};

export default ComposePost;
